import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, useLocation } from "react-router-dom";
import { css, keyframes } from "@emotion/css";
import { SuccessDialog } from "./SuccessDialog";
import { ErrorDialog } from "./ErrorDialog";

// API Base URL
const API_BASE_URL = "https://cmapk.eonlineworkshop.com";

// API service functions
const apiService = {
    // Get country list
    getCountryList: async (countryId = 101) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getCountryList`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    country_id: countryId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching country list:", error);
            throw error;
        }
    },

    // Get state list by country
    getStateList: async (countryId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getStateList`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    country_id: countryId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching state list:", error);
            throw error;
        }
    },

    // Get district list by state
    getDistrictList: async (countryId, stateId) => {
        try {
            const response = await fetch(`${API_BASE_URL}/getDistrictList`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    country_id: countryId,
                    state_id: stateId,
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            return data;
        } catch (error) {
            console.error("Error fetching district list:", error);
            throw error;
        }
    },
};

const spin = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`;

const styles = {
    container: css`
        max-width: 1200px;
        margin: 0 auto;
        padding: 2rem 1rem;
        min-height: calc(100vh - 200px);
        @media (max-width: 768px) {
            padding: 1.5rem 1rem;
        }
    `,
    header: css`
        text-align: center;
        margin-bottom: 3rem;
        color: #1f2937;
        display: flex;
        align-items: center;
        gap: 1rem;
    `,
    backButton: css`
        background: #f3f4f6;
        border: 1px solid #d1d5db;
        color: #374151;
        font-size: 0.875rem;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 0.5rem;
        margin-bottom: 0;
        padding: 10px;
        border-radius: 0.375rem;
        transition: all 0.2s ease;
        &:hover {
            background: #e5e7eb;
            border-color: #9ca3af;
        }
        &:focus {
            outline: 3px solid #2563eb;
            outline-offset: 2px;
        }
        @media (max-width: 768px) {
            padding: 8px;
            font-size: 0.8rem;
        }
    `,
    title: css`
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 0;
        color: #1f2937;
        @media (max-width: 768px) {
            font-size: 16px;
        }
    `,
    form: css`
        background: white;
        padding: 3rem;
        border-radius: 0.5rem;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
        border: 1px solid #e5e7eb;
        @media (max-width: 768px) {
            padding: 2rem 1.5rem;
        }
        @media (max-width: 480px) {
            padding: 1.5rem 1rem;
        }
    `,
    row: css`
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 1.5rem;
        margin-bottom: 1.5rem;
        @media (max-width: 1024px) {
            grid-template-columns: 1fr 1fr;
            gap: 1.25rem;
        }
        @media (max-width: 768px) {
            grid-template-columns: 1fr;
            gap: 1rem;
            margin-bottom: 1rem;
        }
        @media (max-width: 480px) {
            gap: 0.75rem;
            margin-bottom: 0.75rem;
        }
    `,
    fieldGroup: css`
        display: flex;
        flex-direction: column;
        @media (max-width: 480px) {
            margin-bottom: 0.5rem;
        }
    `,
    label: css`
        font-weight: 600;
        color: #374151;
        margin-bottom: 0.5rem;
        font-size: 0.875rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        @media (max-width: 768px) {
            font-size: 0.8rem;
        }
    `,
    required: css`
        color: #dc2626;
        font-weight: 600;
    `,
    input: css`
        padding: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        transition: border-color 0.2s ease;
        background-color: #ffffff;
        width: 100%;
        box-sizing: border-box;
        &:focus {
            outline: 3px solid #2563eb;
            outline-offset: 2px;
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        &.error {
            border-color: #dc2626;
            background-color: #fef2f2;
        }
        &:hover:not(:focus) {
            border-color: #9ca3af;
        }
        @media (max-width: 768px) {
            padding: 0.75rem;
            font-size: 16px;
        }
        @media (max-width: 480px) {
            padding: 0.625rem;
            font-size: 16px;
        }
    `,
    select: css`
        padding: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        background-color: #ffffff;
        transition: border-color 0.2s ease;
        cursor: pointer;
        width: 100%;
        box-sizing: border-box;
        &:focus {
            outline: 3px solid #2563eb;
            outline-offset: 2px;
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        &.error {
            border-color: #dc2626;
            background-color: #fef2f2;
        }
        &:hover:not(:focus) {
            border-color: #9ca3af;
        }
        @media (max-width: 768px) {
            padding: 0.75rem;
            font-size: 16px;
        }
        @media (max-width: 480px) {
            padding: 0.625rem;
            font-size: 16px;
        }
    `,
    textarea: css`
        padding: 0.875rem;
        border: 1px solid #d1d5db;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        transition: border-color 0.2s ease;
        background-color: #ffffff;
        resize: vertical;
        min-height: 100px;
        &:focus {
            outline: 3px solid #2563eb;
            outline-offset: 2px;
            border-color: #2563eb;
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
        }
        &.error {
            border-color: #dc2626;
            background-color: #fef2f2;
        }
        &:hover:not(:focus) {
            border-color: #9ca3af;
        }
        @media (max-width: 768px) {
            padding: 0.75rem;
            font-size: 16px;
            min-height: 80px;
        }
    `,
    fileInput: css`
        padding: 0.875rem;
        border: 1px solid #d1d5db;
        width: 100%;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        background-color: #ffffff;
        transition: border-color 0.2s ease;
        cursor: pointer;
        &:hover {
            border-color: #9ca3af;
        }
        &:focus {
            outline: 3px solid #2563eb;
            outline-offset: 2px;
            border-color: #2563eb;
        }
        &::-webkit-file-upload-button {
            background: #2563eb;
            color: white;
            padding: 0.5rem 1rem;
            border: none;
            border-radius: 0.25rem;
            cursor: pointer;
            margin-right: 1rem;
            font-weight: 500;
            transition: background-color 0.2s ease;
            &:hover {
                background: #1d4ed8;
            }
            &:focus {
                outline: 2px solid #ffffff;
                outline-offset: 1px;
            }
        }
        @media (max-width: 768px) {
            padding: 0.75rem;
            font-size: 16px;
        }
    `,
    errorMessage: css`
        color: #dc2626;
        font-size: 0.75rem;
        margin-top: 0.25rem;
        font-weight: 500;
        @media (max-width: 768px) {
            font-size: 0.7rem;
        }
    `,
    termsSection: css`
        background: #f9fafb;
        padding: 1.5rem;
        border-radius: 0.375rem;
        margin: 1.5rem 0;
        border: 1px solid #e5e7eb;
        @media (max-width: 768px) {
            padding: 1.25rem;
            margin: 1.25rem 0;
        }
    `,
    termsList: css`
        list-style-type: disc;
        margin: 1rem 0 1rem 1.5rem;
        color: #4b5563;
        font-size: 0.875rem;
        line-height: 1.6;
        @media (max-width: 768px) {
            margin: 0.75rem 0 0.75rem 1.25rem;
            font-size: 0.8rem;
        }
    `,
    checkboxGroup: css`
        display: flex;
        align-items: center;
        margin-bottom: 1rem;
        gap: 0.5rem;
        @media (min-width: 768px) {
            margin: 0;
        }

        input[type="checkbox"] {
            margin: 0;
            width: 18px;
            height: 18px;
            cursor: pointer;
        }

        label {
            margin: 0;
            cursor: pointer;
            font-weight: 500;
            color: #374151;
        }
    `,
    checkboxGroupContainer: css`
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        padding: 1rem;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        background-color: #f9fafb;

        @media (min-width: 768px) {
            flex-direction: row;
            gap: 30px;
        }
    `,
    checkbox: css`
        margin-top: 0.125rem;
        width: 1rem;
        height: 1rem;
        accent-color: #2563eb;
        cursor: pointer;
        &:focus {
            outline: 2px solid #2563eb;
            outline-offset: 2px;
        }
        @media (max-width: 768px) {
            width: 1.2rem;
            height: 1.2rem;
        }
    `,
    submitButtonContainer: css`
        display: flex;
        justify-content: flex-end;
        align-items: center;
        @media (max-width: 768px) {
            justify-content: center;
        }
    `,
    submitButton: css`
        background: #2563eb;
        color: white;
        padding: 1rem 2rem;
        border: none;
        border-radius: 0.375rem;
        font-weight: 600;
        font-size: 1rem;
        cursor: pointer;
        transition: background-color 0.2s ease;
        margin-top: 1.5rem;
        &:hover:not(:disabled) {
            background: #1d4ed8;
        }
        &:active:not(:disabled) {
            background: #1e40af;
        }
        &:disabled {
            background: #9ca3af;
            cursor: not-allowed;
        }
        &:focus {
            outline: 3px solid #ffffff;
            outline-offset: 2px;
        }
        @media (max-width: 768px) {
            padding: 0.875rem 1.75rem;
            font-size: 0.9rem;
            width: 100%;
            max-width: 300px;
        }
    `,
    filePreview: css`
        background: #f0f9ff;
        border: 1px solid #0ea5e9;
        border-radius: 0.375rem;
        padding: 0.75rem;
        margin-top: 0.75rem;
        display: flex;
        align-items: center;
        gap: 0.5rem;
        color: #0369a1;
        font-weight: 500;
        font-size: 0.875rem;
        @media (max-width: 768px) {
            padding: 0.5rem;
            font-size: 0.8rem;
        }
    `,
    loadingOverlay: css`
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(255, 255, 255, 0.9);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        backdrop-filter: blur(4px);
    `,
    spinner: css`
        width: 50px;
        height: 50px;
        border: 4px solid #e5e7eb;
        border-top: 4px solid #2563eb;
        border-radius: 50%;
        animation: ${spin} 1s linear infinite;
        margin-bottom: 1rem;
    `,
    loadingText: css`
        font-size: 1.125rem;
        font-weight: 600;
        color: #1f2937;
    `,
    sectionHeader: css`
        font-size: 1.25rem;
        font-weight: 600;
        color: #1f2937;
        margin-bottom: 1.5rem;
        padding-bottom: 0.75rem;
        border-bottom: 1px solid #e5e7eb;
        @media (max-width: 768px) {
            font-size: 1.125rem;
            margin-bottom: 1.25rem;
        }
    `,
    srOnly: css`
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    `,
};

const registrationSchema = z
    .object({
        userTypes: z
            .array(z.string())
            .min(1, "Please select at least one registration type"),
        firstName: z.string().min(1, "First name is required"),
        middleName: z.string().optional(),
        lastName: z.string().min(1, "Last name is required"),
        email: z.string().email("Please enter a valid email address"),
        whatsappNumber: z
            .string()
            .regex(/^[0-9]{10}$/, "WhatsApp number must be exactly 10 digits"),
        contactNumber: z.string().optional(),
        dateOfBirth: z
            .string()
            .refine((val) => val !== "" && val !== undefined, {
                message: "Date of birth is required",
            }),
        gender: z.string().refine((val) => val !== "" && val !== undefined, {
            message: "Gender is required",
        }),
        currentYear: z
            .string()
            .refine((val) => val !== "" && val !== undefined, {
                message: "Current year is required",
            }),
        country: z
            .union([z.string(), z.number()])
            // .refine((val) => val !== "" && val !== undefined, {
            //     message: "Country is required",
            // })
            .optional(),
        state: z
            .union([z.string(), z.number()])
            // .refine((val) => val !== "" && val !== undefined, {
            //     message: "State is required",
            // })
            .optional(),
        district: z
            .union([z.string(), z.number()])
            // .refine((val) => val !== "" && val !== undefined, {
            //     message: "District is required",
            // })
            .optional(),
        city: z
            .string()
            // .min(1, "City is required")
            .optional(),
        pinCode: z
            .string()
            // .regex(
            //     /^[1-9][0-9]{5}$/,
            //     "Pin code must be a valid 6-digit Indian pin code",
            // )
            .optional(),
        collegeName: z.string().refine((val) => val !== "", {
            message: "College name is required",
        }),
        otherCollegeName: z.string().optional(),
        course: z.string().min(1, "Course is required"),
        skills: z.string().optional(),
        termsAccepted: z.boolean().refine((val) => val === true, {
            message: "You must accept the terms and conditions",
        }),
        profilePhoto: z.any().refine((val) => val instanceof File, {
            message: "Profile Photo is required",
        }),
    })
    .refine(
        (data) => {
            if (data.collegeName === "Other") {
                return (
                    data.otherCollegeName && data.otherCollegeName.trim() !== ""
                );
            }
            return true;
        },
        {
            message: "Please enter the college name",
            path: ["otherCollegeName"],
        },
    )
    .refine(
        (data) => {
            if (data.userTypes.includes("talent")) {
                return data.skills && data.skills.trim() !== "";
            }
            return true;
        },
        {
            message: "Skills are required for talent registration",
            path: ["skills"],
        },
    );

const RegistrationForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedFile, setSelectedFile] = useState(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);
    const [successResponse, setSuccessResponse] = useState(null);
    const [formDataForDialog, setFormDataForDialog] = useState(null);
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const [errorData, setErrorData] = useState(null);

    // Location data state
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingDistricts, setLoadingDistricts] = useState(false);
    const [selectedCountryId, setSelectedCountryId] = useState("");
    const [selectedStateId, setSelectedStateId] = useState("");

    // Zod schema for validation
    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        reset,
        trigger,
    } = useForm({
        resolver: zodResolver(registrationSchema),
        mode: "onTouched",
        defaultValues: {
            userTypes: [],
        },
    });

    const watchedUserTypes = watch("userTypes") || [];
    const watchedCollegeName = watch("collegeName");

    // College options
    const collegeOptions = [
        "TRINITY COLLEGE OF ENGINEERING AND RESEARCH",
        "TRINITY ACADEMY OF ENGINEERING",
        "TRINITY INSTITUTE OF MANAGEMENT & RESEARCH",
        "TRINITY COLLEGE POLYTECHNIC",
        "TRINITY INTERNATIONAL SCHOOL",
        "TRINITY JUNIOR COLLEGE",
        "TRINITY COLLEGE OF ARCHITECTURE",
        "TRINITY COLLEGE OF PHARMACY",
        "TRINITY COLLEGE OF ARTS, COMMERCE & SCIENCE",
        "TRINITY COLLEGE OF LAW (PROPOSED)",
        "KJ COLLEGE OF ENGINEERING AND MANAGEMENT RESEARCH",
        "Other",
    ];

    // Get display title based on user type
    const getDisplayTitle = () => {
        const type = location.state?.type;
        switch (type) {
            case "volunteer":
                return "Join as Volunteer";
            case "participant":
                return "Join as Participant";
            case "talent":
                return "Showcase Your Talent";
            default:
                return "Club Mango Registration";
        }
    };

    // Set initial user type from router state
    useEffect(() => {
        const type = location.state?.type;
        if (type) {
            // Set the checkbox value immediately
            setValue("userTypes", [type]);
        } else {
            // Default to participant if no type specified
            setValue("userTypes", ["participant"]);
        }
    }, [location.state, setValue]);

    // Load countries on component mount
    useEffect(() => {
        const loadCountries = async () => {
            setLoadingCountries(true);
            try {
                const response = await apiService.getCountryList();
                if (response.status === "success" && response.response) {
                    setCountries(response.response);
                } else {
                    console.error("Failed to load countries:", response.msg);
                }
            } catch (error) {
                console.error("Error loading countries:", error);
            } finally {
                setLoadingCountries(false);
            }
        };

        loadCountries();
    }, []);

    // Handle checkbox changes for user types
    const handleUserTypeChange = (event) => {
        const { value, checked } = event.target;
        const currentUserTypes = watch("userTypes") || [];

        if (checked) {
            setValue("userTypes", [...currentUserTypes, value]);
        } else {
            setValue(
                "userTypes",
                currentUserTypes.filter((type) => type !== value),
            );
        }
        trigger("userTypes");
    };

    // Handle file selection
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedFile(file);
            setValue("profilePhoto", file);
            trigger("profilePhoto");
        }
    };

    // Handle country change
    const handleCountryChange = async (event) => {
        const countryId = event.target.value;
        setValue("country", countryId);
        setSelectedCountryId(countryId);

        // Reset state and district when country changes
        setValue("state", "");
        setValue("district", "");
        setSelectedStateId("");
        setStates([]);
        setDistricts([]);

        await trigger("country");

        // Load states for selected country
        if (countryId) {
            setLoadingStates(true);
            try {
                const response = await apiService.getStateList(countryId);
                if (response.status === "success" && response.response) {
                    setStates(response.response);
                } else {
                    console.error("Failed to load states:", response.msg);
                }
            } catch (error) {
                console.error("Error loading states:", error);
            } finally {
                setLoadingStates(false);
            }
        }
    };

    // Handle state change
    const handleStateChange = async (event) => {
        const stateId = event.target.value;
        setValue("state", stateId);
        setSelectedStateId(stateId);

        // Reset district when state changes
        setValue("district", "");
        setDistricts([]);

        await trigger("state");

        // Load districts for selected state
        if (stateId && selectedCountryId) {
            setLoadingDistricts(true);
            try {
                const response = await apiService.getDistrictList(
                    selectedCountryId,
                    stateId,
                );
                if (response.status === "success" && response.response) {
                    setDistricts(response.response);
                } else {
                    console.error("Failed to load districts:", response.msg);
                }
            } catch (error) {
                console.error("Error loading districts:", error);
            } finally {
                setLoadingDistricts(false);
            }
        }
    };

    // Handle district change
    const handleDistrictChange = async (event) => {
        const districtId = event.target.value;
        setValue("district", districtId);
        await trigger("district");
    };

    const onSubmit = async (formData) => {
        console.log("Form submission started with data:", formData);
        console.log("Selected file:", selectedFile);

        setIsSubmitting(true);

        try {
            // Handle college name logic
            let finalCollegeName = "";
            if (formData.collegeName === "Other") {
                finalCollegeName = formData.otherCollegeName.trim();
            } else {
                finalCollegeName = formData.collegeName;
            }

            // Get system information
            const getSystemInfo = () => {
                const userAgent = navigator.userAgent;
                let os = "Unknown";
                let device = "Unknown";
                let browser = "Unknown";

                // Detect OS
                if (userAgent.includes("Windows")) os = "Windows";
                else if (userAgent.includes("Mac")) os = "Mac";
                else if (userAgent.includes("Linux")) os = "Linux";
                else if (userAgent.includes("Android")) os = "Android";
                else if (userAgent.includes("iOS")) os = "iOS";

                // Detect device
                if (userAgent.includes("Mobile")) device = "Mobile";
                else if (userAgent.includes("Tablet")) device = "Tablet";
                else device = "Desktop";

                // Detect browser
                if (userAgent.includes("Chrome")) browser = "Chrome";
                else if (userAgent.includes("Firefox")) browser = "Firefox";
                else if (userAgent.includes("Safari")) browser = "Safari";
                else if (userAgent.includes("Edge")) browser = "Edge";

                return { os, device, browser };
            };

            const systemInfo = getSystemInfo();

            // Prepare data according to new API specification
            const data = new FormData();
            data.append("newRegister", "true");
            data.append("role", JSON.stringify(formData.userTypes)); // Send as JSON array
            data.append("email_id1", formData.email);
            data.append("email_id2", finalCollegeName);
            data.append("whatsapp_mobile", formData.whatsappNumber);
            data.append(
                "mobile_no",
                formData.contactNumber || formData.whatsappNumber,
            );
            data.append("first_name", formData.firstName);
            data.append("middle_name", formData.middleName || "");
            data.append("last_name", formData.lastName);
            data.append("member_dob", formData.dateOfBirth);
            data.append("member_gender", formData.gender);
            data.append("occupation", formData.course);

            // data.append("country", formData.country);
            // data.append("state", formData.state);
            // data.append("district", formData.district);
            // data.append("city", formData.city);
            // data.append("pinCode", formData.pinCode);

            data.append("country", "");
            data.append("state", "");
            data.append("district", "");
            data.append("city", "");
            data.append("pinCode", "");

            data.append("reference_name", formData.currentYear);
            data.append("os", systemInfo.os);
            data.append("device", systemInfo.device);
            data.append("browser", systemInfo.browser);
            data.append("user_id", "1"); // Hardcoded as per specification
            data.append("talent_skills", formData.skills || "");

            // Handle profile photo with new field name
            if (selectedFile) {
                data.append("member_pic", selectedFile);
            }

            console.log("FormData prepared, sending request...");

            // Make API call to the correct endpoint
            const response = await fetch(
                "https://cmapk.eonlineworkshop.com/web/signup",
                {
                    method: "POST",
                    body: data,
                },
            );

            if (response.ok) {
                const result = await response.json();
                console.log("Registration successful:", result);

                if (result.status === "success") {
                    setSuccessResponse(result.response);
                    setFormDataForDialog(formData); // Store form data for dialog
                    setShowSuccessDialog(true);
                } else {
                    // Show error dialog instead of toast
                    setErrorData({
                        status: result.status,
                        statusCode: result.statusCode || "N/A",
                        msg: result.msg || "Registration failed",
                        altMsg: result.altMsg || "",
                    });
                    setShowErrorDialog(true);
                }
            } else {
                const errorData = await response.json();
                // Show error dialog instead of toast
                setErrorData({
                    status: "failed",
                    statusCode: response.status,
                    msg: errorData.msg || "Registration failed",
                    altMsg: errorData.altMsg || "",
                });
                setShowErrorDialog(true);
                console.error("Registration failed:", errorData);
            }
        } catch (error) {
            console.error("Registration error:", error);
            // Show error dialog instead of toast
            setErrorData({
                status: "error",
                statusCode: "N/A",
                msg: "An error occurred during registration",
                altMsg: error.message || "Please try again",
            });
            setShowErrorDialog(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoHome = () => {
        setShowSuccessDialog(false);
        setSuccessResponse(null);
        setFormDataForDialog(null);
        navigate("/");
    };

    return (
        <>
            {/* Skip to main content link for screen readers */}
            <a
                href="#main-content"
                className={styles.srOnly}
                style={{
                    position: "absolute",
                    top: "10px",
                    left: "10px",
                    zIndex: 1000,
                    padding: "8px 16px",
                    backgroundColor: "#2563eb",
                    color: "white",
                    textDecoration: "none",
                    borderRadius: "4px",
                    fontSize: "14px",
                    fontWeight: "600",
                }}
                onFocus={(e) => {
                    e.target.style.position = "static";
                    e.target.style.width = "auto";
                    e.target.style.height = "auto";
                }}
                onBlur={(e) => {
                    e.target.style.position = "absolute";
                    e.target.style.width = "1px";
                    e.target.style.height = "1px";
                }}
            >
                Skip to main content
            </a>

            <div
                className={styles.container}
                id="main-content"
                role="main"
                tabIndex="-1"
            >
                <div className={styles.header}>
                    <button
                        className={styles.backButton}
                        onClick={() => navigate("/")}
                        aria-label="Go back to home page"
                    >
                        ←
                    </button>
                    <h1 className={styles.title}>{getDisplayTitle()}</h1>
                </div>

                <form
                    className={styles.form}
                    onSubmit={handleSubmit(onSubmit, (errors) => {
                        // Handle validation errors
                        console.log("Form validation failed:", errors);

                        // Count validation errors
                        const errorCount = Object.keys(errors).length;
                        const errorMessage =
                            errorCount === 1
                                ? "Please correct the form field and try again."
                                : `Please correct ${errorCount} form fields and try again.`;

                        // Log specific field errors for debugging
                        Object.keys(errors).forEach((fieldName) => {
                            console.log(
                                `${fieldName} error:`,
                                errors[fieldName]?.message,
                            );
                        });

                        // Announce errors to screen readers
                        const errorAnnouncement = document.createElement("div");
                        errorAnnouncement.setAttribute("aria-live", "polite");
                        errorAnnouncement.setAttribute("aria-atomic", "true");
                        errorAnnouncement.className = "sr-only";
                        errorAnnouncement.textContent = errorMessage;
                        document.body.appendChild(errorAnnouncement);

                        // Remove after announcement
                        setTimeout(() => {
                            document.body.removeChild(errorAnnouncement);
                        }, 1000);
                    })}
                    aria-label="Club Mango Friends Registration Form"
                    noValidate
                >
                    {/* User Type Selection */}
                    <fieldset style={{ marginBottom: "2.5rem" }}>
                        <legend className={styles.sectionHeader}>
                            Registration Type
                        </legend>
                        <div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="userType"
                                    className={styles.label}
                                    id="userType-label"
                                >
                                    Registration Type{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <div className={styles.checkboxGroupContainer}>
                                    <div className={styles.checkboxGroup}>
                                        <input
                                            type="checkbox"
                                            id="participant"
                                            name="userTypes"
                                            value="participant"
                                            className={styles.checkbox}
                                            checked={watchedUserTypes.includes(
                                                "participant",
                                            )}
                                            onChange={handleUserTypeChange}
                                            aria-labelledby="userType-label"
                                            aria-describedby={
                                                errors.userTypes
                                                    ? "userType-error"
                                                    : undefined
                                            }
                                            aria-invalid={
                                                errors.userTypes
                                                    ? "true"
                                                    : "false"
                                            }
                                            aria-required="true"
                                        />
                                        <label
                                            htmlFor="participant"
                                            className={styles.label}
                                        >
                                            Join as Participant
                                        </label>
                                    </div>
                                    <div className={styles.checkboxGroup}>
                                        <input
                                            type="checkbox"
                                            id="volunteer"
                                            name="userTypes"
                                            value="volunteer"
                                            className={styles.checkbox}
                                            checked={watchedUserTypes.includes(
                                                "volunteer",
                                            )}
                                            onChange={handleUserTypeChange}
                                            aria-labelledby="userType-label"
                                            aria-describedby={
                                                errors.userTypes
                                                    ? "userType-error"
                                                    : undefined
                                            }
                                            aria-invalid={
                                                errors.userTypes
                                                    ? "true"
                                                    : "false"
                                            }
                                            aria-required="true"
                                        />
                                        <label
                                            htmlFor="volunteer"
                                            className={styles.label}
                                        >
                                            Join as Volunteer
                                        </label>
                                    </div>
                                    <div className={styles.checkboxGroup}>
                                        <input
                                            type="checkbox"
                                            id="talent"
                                            name="userTypes"
                                            value="talent"
                                            className={styles.checkbox}
                                            checked={watchedUserTypes.includes(
                                                "talent",
                                            )}
                                            onChange={handleUserTypeChange}
                                            aria-labelledby="userType-label"
                                            aria-describedby={
                                                errors.userTypes
                                                    ? "userType-error"
                                                    : undefined
                                            }
                                            aria-invalid={
                                                errors.userTypes
                                                    ? "true"
                                                    : "false"
                                            }
                                            aria-required="true"
                                        />
                                        <label
                                            htmlFor="talent"
                                            className={styles.label}
                                        >
                                            Showcase Your Talent
                                        </label>
                                    </div>
                                </div>
                                {errors.userTypes && (
                                    <span
                                        id="userType-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.userTypes.message}
                                    </span>
                                )}
                            </div>
                        </div>
                    </fieldset>

                    {/* Personal Information Section */}
                    <fieldset style={{ marginBottom: "2.5rem" }}>
                        <legend className={styles.sectionHeader}>
                            Personal Information
                        </legend>
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="firstName"
                                    className={styles.label}
                                    id="firstName-label"
                                >
                                    First Name{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="firstName"
                                    name="firstName"
                                    type="text"
                                    className={`${styles.input} ${
                                        errors.firstName ? styles.error : ""
                                    }`}
                                    {...register("firstName")}
                                    placeholder="Enter your first name"
                                    aria-labelledby="firstName-label"
                                    aria-describedby={
                                        errors.firstName
                                            ? "firstName-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.firstName ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.firstName && (
                                    <span
                                        id="firstName-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.firstName.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="middleName"
                                    className={styles.label}
                                    id="middleName-label"
                                >
                                    Middle Name (Optional)
                                </label>
                                <input
                                    id="middleName"
                                    name="middleName"
                                    type="text"
                                    className={styles.input}
                                    {...register("middleName")}
                                    placeholder="Enter your middle name"
                                    aria-labelledby="middleName-label"
                                    aria-describedby={
                                        errors.middleName
                                            ? "middleName-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.middleName ? "true" : "false"
                                    }
                                />
                                {errors.middleName && (
                                    <span
                                        id="middleName-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.middleName.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="lastName"
                                    className={styles.label}
                                    id="lastName-label"
                                >
                                    Last Name{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="lastName"
                                    name="lastName"
                                    type="text"
                                    className={`${styles.input} ${
                                        errors.lastName ? styles.error : ""
                                    }`}
                                    {...register("lastName")}
                                    placeholder="Enter your last name"
                                    aria-labelledby="lastName-label"
                                    aria-describedby={
                                        errors.lastName
                                            ? "lastName-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.lastName ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.lastName && (
                                    <span
                                        id="lastName-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.lastName.message}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Contact Information Fields */}
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="email"
                                    className={styles.label}
                                    id="email-label"
                                >
                                    Email Address{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className={`${styles.input} ${
                                        errors.email ? styles.error : ""
                                    }`}
                                    {...register("email")}
                                    placeholder="Enter your email address"
                                    aria-labelledby="email-label"
                                    aria-describedby={
                                        errors.email ? "email-error" : undefined
                                    }
                                    aria-invalid={
                                        errors.email ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.email && (
                                    <span
                                        id="email-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.email.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="whatsappNumber"
                                    className={styles.label}
                                    id="whatsappNumber-label"
                                >
                                    WhatsApp Number{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="whatsappNumber"
                                    name="whatsappNumber"
                                    type="tel"
                                    className={`${styles.input} ${
                                        errors.whatsappNumber
                                            ? styles.error
                                            : ""
                                    }`}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(
                                            /[^0-9]/g,
                                            "",
                                        );
                                    }}
                                    {...register("whatsappNumber")}
                                    placeholder="Enter your WhatsApp number"
                                    aria-labelledby="whatsappNumber-label"
                                    aria-describedby={
                                        errors.whatsappNumber
                                            ? "whatsappNumber-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.whatsappNumber ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.whatsappNumber && (
                                    <span
                                        id="whatsappNumber-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.whatsappNumber.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="contactNumber"
                                    className={styles.label}
                                    id="contactNumber-label"
                                >
                                    Contact Number (Optional)
                                </label>
                                <input
                                    id="contactNumber"
                                    name="contactNumber"
                                    type="tel"
                                    className={styles.input}
                                    {...register("contactNumber")}
                                    placeholder="Enter your contact number"
                                    aria-labelledby="contactNumber-label"
                                    aria-describedby={
                                        errors.contactNumber
                                            ? "contactNumber-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.contactNumber ? "true" : "false"
                                    }
                                />
                                {errors.contactNumber && (
                                    <span
                                        id="contactNumber-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.contactNumber.message}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Demographics Fields */}
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="dateOfBirth"
                                    className={styles.label}
                                    id="dateOfBirth-label"
                                >
                                    Date of Birth{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="dateOfBirth"
                                    name="dateOfBirth"
                                    type="date"
                                    className={`${styles.input} ${
                                        errors.dateOfBirth ? styles.error : ""
                                    }`}
                                    {...register("dateOfBirth")}
                                    aria-labelledby="dateOfBirth-label"
                                    aria-describedby={
                                        errors.dateOfBirth
                                            ? "dateOfBirth-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.dateOfBirth ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.dateOfBirth && (
                                    <span
                                        id="dateOfBirth-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.dateOfBirth.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="gender"
                                    className={styles.label}
                                    id="gender-label"
                                >
                                    Gender{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="gender"
                                    name="gender"
                                    className={`${styles.select} ${
                                        errors.gender ? styles.error : ""
                                    }`}
                                    {...register("gender")}
                                    aria-labelledby="gender-label"
                                    aria-describedby={
                                        errors.gender
                                            ? "gender-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.gender ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">Select Gender</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="other">Other</option>
                                </select>
                                {errors.gender && (
                                    <span
                                        id="gender-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.gender.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}></div>
                        </div>
                    </fieldset>

                    {/* Academic Information Section */}
                    <fieldset style={{ marginBottom: "2.5rem" }}>
                        <legend className={styles.sectionHeader}>
                            Academic Information
                        </legend>
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="collegeName"
                                    className={styles.label}
                                    id="collegeName-label"
                                >
                                    College Name{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="collegeName"
                                    name="collegeName"
                                    className={`${styles.select} ${
                                        errors.collegeName ? styles.error : ""
                                    }`}
                                    {...register("collegeName")}
                                    aria-labelledby="collegeName-label"
                                    aria-describedby={
                                        errors.collegeName
                                            ? "collegeName-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.collegeName ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">Select College</option>
                                    {collegeOptions.map((college) => (
                                        <option key={college} value={college}>
                                            {college}
                                        </option>
                                    ))}
                                </select>
                                {errors.collegeName && (
                                    <span
                                        id="collegeName-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.collegeName.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="course"
                                    className={styles.label}
                                    id="course-label"
                                >
                                    Course/Stream{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="course"
                                    name="course"
                                    type="text"
                                    className={`${styles.input} ${
                                        errors.course ? styles.error : ""
                                    }`}
                                    {...register("course")}
                                    placeholder="e.g., Computer Science"
                                    aria-labelledby="course-label"
                                    aria-describedby={
                                        errors.course
                                            ? "course-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.course ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.course && (
                                    <span
                                        id="course-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.course.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="currentYear"
                                    className={styles.label}
                                    id="currentYear-label"
                                >
                                    Current Year{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="currentYear"
                                    name="currentYear"
                                    className={`${styles.select} ${
                                        errors.currentYear ? styles.error : ""
                                    }`}
                                    {...register("currentYear")}
                                    aria-labelledby="currentYear-label"
                                    aria-describedby={
                                        errors.currentYear
                                            ? "currentYear-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.currentYear ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">Select Year</option>
                                    <option value="First Year">
                                        First Year
                                    </option>
                                    <option value="Second Year">
                                        Second Year
                                    </option>
                                    <option value="Third Year">
                                        Third Year
                                    </option>
                                    <option value="Fourth Year">
                                        Fourth Year
                                    </option>
                                </select>
                                {errors.currentYear && (
                                    <span
                                        id="currentYear-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.currentYear.message}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Other College Name Field */}
                        {watchedCollegeName === "Other" && (
                            <div className={styles.row}>
                                <div className={styles.fieldGroup}>
                                    <label
                                        htmlFor="otherCollegeName"
                                        className={styles.label}
                                        id="otherCollegeName-label"
                                    >
                                        Other College Name{" "}
                                        <span
                                            className={styles.required}
                                            aria-label="required"
                                        >
                                            *
                                        </span>
                                    </label>
                                    <input
                                        id="otherCollegeName"
                                        name="otherCollegeName"
                                        type="text"
                                        className={`${styles.input} ${
                                            errors.otherCollegeName
                                                ? styles.error
                                                : ""
                                        }`}
                                        {...register("otherCollegeName")}
                                        placeholder="Enter your college name"
                                        aria-labelledby="otherCollegeName-label"
                                        aria-describedby={
                                            errors.otherCollegeName
                                                ? "otherCollegeName-error"
                                                : undefined
                                        }
                                        aria-invalid={
                                            errors.otherCollegeName
                                                ? "true"
                                                : "false"
                                        }
                                        aria-required="true"
                                    />
                                    {errors.otherCollegeName && (
                                        <span
                                            id="otherCollegeName-error"
                                            className={styles.errorMessage}
                                            role="alert"
                                            aria-live="polite"
                                        >
                                            {errors.otherCollegeName.message}
                                        </span>
                                    )}
                                </div>
                                <div className={styles.fieldGroup}></div>
                                <div className={styles.fieldGroup}></div>
                            </div>
                        )}

                        {/* Skills Field for Talent */}
                        {watchedUserTypes &&
                            watchedUserTypes.includes("talent") && (
                                <div>
                                    <div className={styles.fieldGroup}>
                                        <label
                                            htmlFor="skills"
                                            id="skills-label"
                                            className={styles.label}
                                        >
                                            Skills & Talents{" "}
                                            <span className={styles.required}>
                                                *
                                            </span>
                                        </label>
                                        <textarea
                                            id="skills"
                                            name="skills"
                                            className={`${styles.textarea} ${
                                                errors.skills
                                                    ? styles.error
                                                    : ""
                                            }`}
                                            placeholder="Describe your skills, talents, or special abilities..."
                                            {...register("skills")}
                                            aria-labelledby="skills-label"
                                            aria-describedby={
                                                errors.skills
                                                    ? "skills-error"
                                                    : undefined
                                            }
                                            aria-invalid={
                                                errors.skills ? "true" : "false"
                                            }
                                            aria-required="true"
                                            rows={4}
                                        />
                                        {errors.skills && (
                                            <span
                                                id="skills-error"
                                                className={styles.errorMessage}
                                                role="alert"
                                                aria-live="polite"
                                            >
                                                {errors.skills.message}
                                            </span>
                                        )}
                                    </div>
                                    <div className={styles.fieldGroup}></div>
                                </div>
                            )}
                    </fieldset>

                    {/* Location Section */}
                    {/* <fieldset style={{ marginBottom: "2.5rem" }}>
                        <legend className={styles.sectionHeader}>
                            Location Information
                        </legend>
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="country"
                                    className={styles.label}
                                    id="country-label"
                                >
                                    Country{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="country"
                                    name="country"
                                    className={`${styles.select} ${
                                        errors.country ? styles.error : ""
                                    }`}
                                    onChange={handleCountryChange}
                                    disabled={loadingCountries}
                                    aria-labelledby="country-label"
                                    aria-describedby={
                                        errors.country
                                            ? "country-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.country ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">
                                        {loadingCountries
                                            ? "Loading countries..."
                                            : "Select Country"}
                                    </option>
                                    {countries.map((country) => (
                                        <option
                                            key={country.country_id}
                                            value={country.country_id}
                                        >
                                            {country.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.country && (
                                    <span
                                        id="country-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.country.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="state"
                                    className={styles.label}
                                    id="state-label"
                                >
                                    State{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="state"
                                    name="state"
                                    className={`${styles.select} ${
                                        errors.state ? styles.error : ""
                                    }`}
                                    onChange={handleStateChange}
                                    disabled={
                                        loadingStates || !selectedCountryId
                                    }
                                    aria-labelledby="state-label"
                                    aria-describedby={
                                        errors.state ? "state-error" : undefined
                                    }
                                    aria-invalid={
                                        errors.state ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">
                                        {loadingStates
                                            ? "Loading states..."
                                            : !selectedCountryId
                                              ? "Select country first"
                                              : "Select State"}
                                    </option>
                                    {states.map((state) => (
                                        <option
                                            key={state.stateId}
                                            value={state.stateId}
                                        >
                                            {state.stateName}
                                        </option>
                                    ))}
                                </select>
                                {errors.state && (
                                    <span
                                        id="state-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.state.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="district"
                                    className={styles.label}
                                    id="district-label"
                                >
                                    District{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <select
                                    id="district"
                                    name="district"
                                    className={`${styles.select} ${
                                        errors.district ? styles.error : ""
                                    }`}
                                    onChange={handleDistrictChange}
                                    disabled={
                                        loadingDistricts || !selectedStateId
                                    }
                                    aria-labelledby="district-label"
                                    aria-describedby={
                                        errors.district
                                            ? "district-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.district ? "true" : "false"
                                    }
                                    aria-required="true"
                                >
                                    <option value="">
                                        {loadingDistricts
                                            ? "Loading districts..."
                                            : !selectedStateId
                                              ? "Select state first"
                                              : "Select District"}
                                    </option>
                                    {districts.map((district) => (
                                        <option
                                            key={district.id}
                                            value={district.id}
                                        >
                                            {district.name}
                                        </option>
                                    ))}
                                </select>
                                {errors.district && (
                                    <span
                                        id="district-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.district.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="city"
                                    className={styles.label}
                                    id="city-label"
                                >
                                    City{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="city"
                                    name="city"
                                    type="text"
                                    className={`${styles.input} ${
                                        errors.city ? styles.error : ""
                                    }`}
                                    {...register("city")}
                                    placeholder="Enter your city"
                                    aria-labelledby="city-label"
                                    aria-describedby={
                                        errors.city ? "city-error" : undefined
                                    }
                                    aria-invalid={
                                        errors.city ? "true" : "false"
                                    }
                                />
                                {errors.city && (
                                    <span
                                        id="city-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.city.message}
                                    </span>
                                )}
                            </div>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="pinCode"
                                    className={styles.label}
                                    id="pinCode-label"
                                >
                                    Pin Code{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="pinCode"
                                    name="pinCode"
                                    type="text"
                                    className={`${styles.input} ${
                                        errors.pinCode ? styles.error : ""
                                    }`}
                                    {...register("pinCode")}
                                    placeholder="Enter 6-digit pin code"
                                    aria-labelledby="pinCode-label"
                                    aria-describedby={
                                        errors.pinCode
                                            ? "pinCode-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.pinCode ? "true" : "false"
                                    }
                                    inputMode="numeric"
                                    maxLength={6}
                                    onInput={(e) => {
                                        e.target.value = e.target.value.replace(
                                            /[^0-9]/g,
                                            "",
                                        );
                                    }}
                                />
                                {errors.pinCode && (
                                    <span
                                        id="pinCode-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.pinCode.message}
                                    </span>
                                )}
                            </div>
                        </div>
                    </fieldset> */}

                    {/* Profile Photo Section */}
                    <fieldset style={{ marginBottom: "2.5rem" }}>
                        <legend className={styles.sectionHeader}>
                            Profile Photo
                        </legend>
                        <div className={styles.row}>
                            <div className={styles.fieldGroup}>
                                <label
                                    htmlFor="profilePhoto"
                                    className={styles.label}
                                    id="profilePhoto-label"
                                >
                                    Profile Photo{" "}
                                    <span
                                        className={styles.required}
                                        aria-label="required"
                                    >
                                        *
                                    </span>
                                </label>
                                <input
                                    id="profilePhoto"
                                    name="profilePhoto"
                                    type="file"
                                    accept="image/*"
                                    className={styles.fileInput}
                                    onChange={handleFileChange}
                                    aria-labelledby="profilePhoto-label"
                                    aria-describedby={
                                        errors.profilePhoto
                                            ? "profilePhoto-error"
                                            : undefined
                                    }
                                    aria-invalid={
                                        errors.profilePhoto ? "true" : "false"
                                    }
                                    aria-required="true"
                                />
                                {errors.profilePhoto && (
                                    <span
                                        id="profilePhoto-error"
                                        className={styles.errorMessage}
                                        role="alert"
                                        aria-live="polite"
                                    >
                                        {errors.profilePhoto.message}
                                    </span>
                                )}
                                {selectedFile && (
                                    <div className={styles.filePreview}>
                                        📎 Selected: {selectedFile.name}
                                    </div>
                                )}
                            </div>
                            <div className={styles.fieldGroup}></div>
                            <div className={styles.fieldGroup}></div>
                        </div>
                    </fieldset>

                    {/* Terms and Conditions Section */}
                    <div className={styles.termsSection}>
                        <h3 className={styles.sectionHeader}>
                            Terms & Conditions
                        </h3>
                        <ul className={styles.termsList}>
                            <li>
                                By registering, you agree to participate
                                actively in Club Mango activities and events.
                            </li>
                            <li>
                                You commit to maintaining a positive and
                                respectful attitude towards all members.
                            </li>
                            <li>
                                Your personal information will be used solely
                                for Club Mango activities and will not be shared
                                with third parties.
                            </li>
                            <li>
                                You understand that participation in events is
                                voluntary and you are responsible for your own
                                safety.
                            </li>
                            <li>
                                Club Mango reserves the right to modify these
                                terms and conditions as needed.
                            </li>
                        </ul>

                        <div className={styles.checkboxGroup}>
                            <input
                                type="checkbox"
                                id="termsAccepted"
                                name="termsAccepted"
                                className={styles.checkbox}
                                {...register("termsAccepted")}
                                aria-describedby={
                                    errors.termsAccepted
                                        ? "termsAccepted-error"
                                        : undefined
                                }
                                aria-invalid={
                                    errors.termsAccepted ? "true" : "false"
                                }
                                aria-required="true"
                            />
                            <label
                                htmlFor="termsAccepted"
                                className={styles.label}
                                style={{ marginBottom: 0 }}
                            >
                                I have read and agree to the Terms and
                                Conditions{" "}
                                <span className={styles.required}>*</span>
                            </label>
                        </div>
                        {errors.termsAccepted && (
                            <span
                                id="termsAccepted-error"
                                className={styles.errorMessage}
                                role="alert"
                                aria-live="polite"
                            >
                                {errors.termsAccepted.message}
                            </span>
                        )}
                    </div>

                    <div className={styles.submitButtonContainer}>
                        <button
                            type="submit"
                            className={styles.submitButton}
                            disabled={isSubmitting}
                        >
                            {isSubmitting
                                ? "Submitting..."
                                : "Submit Registration"}
                        </button>
                    </div>

                    {isSubmitting && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.spinner}></div>
                            <div className={styles.loadingText}>
                                Processing your registration...
                            </div>
                            <div
                                style={{
                                    marginTop: "0.5rem",
                                    color: "#6b7280",
                                    fontSize: "0.875rem",
                                }}
                            >
                                Please do not close this window
                            </div>
                        </div>
                    )}
                </form>
            </div>

            {/* Success Dialog */}
            <SuccessDialog
                isOpen={showSuccessDialog}
                onClose={() => setShowSuccessDialog(false)}
                responseData={successResponse}
                formData={formDataForDialog}
                onGoHome={handleGoHome}
            />

            {/* Error Dialog */}
            <ErrorDialog
                isOpen={showErrorDialog}
                onClose={() => setShowErrorDialog(false)}
                errorData={errorData}
            />
        </>
    );
};

export default RegistrationForm;
